/**
 * "Safety register" (help `cloud.safety-register`; MB's decisions 837 and 863; docs/specs/safety-register.md §6): the
 * organisation's accidents, incidents, near misses and landholder complaints, newest first, with TJR's totals,
 * filters by project, kind and open or closed, an entry's details, and "Print the form" (TJR's Safety Occurrence
 * Notification or Landholder Notification, laid out for the browser's print so a PDF can be saved). Every staff role
 * with access to the project reads it; clients never. Since decision 863 (3 B) entries are recorded, changed, closed,
 * reopened and removed here too, with the same checks as Teslyn Field (`checkSafetyEntry`) and the same rule of who
 * records which kinds (`canRecordSafety`: office staff record landholder complaints, 2 B); the database's policies
 * enforce it, stamp who recorded the entry and audit each step, and the project's laptops get the change when they
 * sync. An entry links the project's photos (5 B), named on the entry and its form.
 *
 * Decision 1021 (R205): each entry of the projects' Height breach reports is listed too, of kind "Below lower working
 * limit" or "Below hard deck", read from the breach report (`registerRecordFromBreach`), never typed here: read only,
 * with "From the height breach report" and "Open breach report" (MB's R219, decision 1034: the project's latest
 * report PDF, sent by a laptop at its sync, in a new tab through a signed link; while none has been sent, the
 * project's Safety documents tab, where the report's entries and charts are); closed once its reason is
 * recorded; one removed from the report shows as withdrawn, with who and why.
 *
 * Decision 1023, named so by R220: every record has "Approve for client" and "Withdraw from client" for tenant admins
 * and managers (`canShowRegisterToClient`); the list says which are approved ("Approved for client") and filters by it
 * ("Client"); the client reads the whole of an approved record (decision 1035) on the client portal's Safety register
 * page (`ClientSafetyRegisterScreen`).
 */
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  BREACH_RECORD_READ_ONLY,
  BREACH_RECORD_SOURCE_WORDS,
  BREACH_REPORT_OPEN_WORDS,
  REGISTER_KINDS,
  REGISTER_KIND_TOTAL_WORDS,
  REGISTER_KIND_WORDS,
  REGISTER_SHOW,
  REGISTER_SHOW_WORDS,
  SAFETY_KINDS,
  SAFETY_KIND_WORDS,
  SAFETY_LIMITS,
  SAFETY_WHO_RECORDS,
  REGISTER_APPROVED_WORDS,
  REGISTER_CLIENT_FILTER,
  REGISTER_CLIENT_FILTER_WORDS,
  REGISTER_CLIENT_SEES,
  REGISTER_CLIENT_STEP_WORDS,
  canChangeRegisterRecord,
  registerClientDoneWords,
  registerClientLine,
  registerClientShown,
  registerClientStep,
  registerDetailLines,
  registerSourceOf,
  checkSafetyEntry,
  jspPrintedDate,
  livePhotos,
  newSafetyEntry,
  photoCategoryLabel,
  photoTakenText,
  projectCrewOn,
  safetyActionWords,
  safetyFormContent,
  registerRecordShown,
  registerRecordState,
  registerSafetyEntry,
  safetyKindsFor,
  safetyOrder,
  safetyTotals,
  type ProjectPhoto,
  type RegisterClientFilter,
  type RegisterClientStep,
  type RegisterKind,
  type RegisterShow,
  type SafetyEntry,
  type SafetyEntryDraft,
  type SafetyKind,
  type SafetyPerson,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { CheckField, IssueList, SelectField, TextArea, TextField } from '../components/Fields.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import { loadBreachReportPdf } from '../deliverables/breachReportApi.ts';
import { documentUrl, openInNewTab } from '../document-viewer/open.ts';
import {
  closeSafetyEntry,
  loadLinkedPhotos,
  loadSafetyChoices,
  loadSafetyEntries,
  loadSafetyProjects,
  myStaffName,
  removeSafetyEntry,
  saveSafetyEntry,
  setRegisterShown,
  type LinkedPhoto,
  type SafetyChoices,
  type SafetyListRow,
  type SafetyProject,
} from './register-api.ts';
import { TableWrap } from '../components/TableWrap.tsx';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));
const printed = (iso: string | null): string => (iso ? jspPrintedDate(iso.slice(0, 10)) : '');

export interface SafetyRegisterScreenProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly tenantName: string;
  /** The viewer's tenant roles: who records which kinds (display only; the database decides). */
  readonly roles?: readonly string[];
  /** Today (YYYY-MM-DD) for overdue follow-ups and a new entry's date; tests pass their own. */
  readonly today?: string;
  /** The browser's print; tests pass their own. */
  readonly print?: () => void;
  /** Open an address in a new tab: the breach report's PDF (decision 1034); tests pass their own. */
  readonly openTab?: (url: string) => void;
  /**
   * "Open breach report" while no report PDF has been sent (decision 1021, R219): open the project's Safety tab, where
   * its Height breach report's entries are (else the link's own address).
   */
  readonly onOpenBreachReport?: (projectId: string) => void;
}

/** The project's Safety tab, where its Height breach report is (`?page=projects&project=<id>&tab=safety`). */
export function breachReportHref(projectId: string): string {
  return `?page=projects&project=${encodeURIComponent(projectId)}&tab=safety`;
}

/** An entry being written: its id (null for a new one), its project and its fields. */
interface Editing {
  readonly id: string | null;
  readonly projectId: string;
  readonly draft: SafetyEntryDraft;
}

function draftOf(e: SafetyEntry): SafetyEntryDraft {
  return {
    kind: e.kind,
    occurred_on: e.occurred_on,
    occurred_at: e.occurred_at,
    topic: e.topic,
    reported_by_person_id: e.reported_by_person_id,
    reported_by_name: e.reported_by_name,
    reported_by_role: e.reported_by_role,
    people: e.people,
    aircraft_id: e.aircraft_id,
    aircraft_registration: e.aircraft_registration,
    location: e.location,
    lat: e.lat,
    lon: e.lon,
    land_parcel_id: e.land_parcel_id,
    landholder_name: e.landholder_name,
    landholder_details: e.landholder_details,
    landholder_phone: e.landholder_phone,
    description: e.description,
    actions: e.actions,
    corrective_action_taken: e.corrective_action_taken,
    follow_up: e.follow_up,
    follow_up_due: e.follow_up_due,
    photo_ids: e.photo_ids ?? [],
  };
}

export function SafetyRegisterScreen({
  sb,
  tenantId,
  tenantName,
  roles = [],
  today = new Date().toISOString().slice(0, 10),
  print = () => window.print(),
  openTab = openInNewTab,
  onOpenBreachReport,
}: SafetyRegisterScreenProps): ReactElement {
  const [rows, setRows] = useState<SafetyListRow[] | null>(null);
  const [photos, setPhotos] = useState<LinkedPhoto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [project, setProject] = useState('all');
  const [kind, setKind] = useState<'all' | RegisterKind>('all');
  const [open, setOpen] = useState<RegisterShow>('all');
  // R220: approved for the client or not
  const [client, setClient] = useState<RegisterClientFilter>('all');
  const [selected, setSelected] = useState<string | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [me, setMe] = useState('');
  // the form laid out for printing stays on the page (hidden on screen); each press asks the browser to print once
  const [printing, setPrinting] = useState<string | null>(null);
  const [printAsk, setPrintAsk] = useState(0);
  // decision 863 (2 B): office staff record landholder complaints only; the other staff roles every kind
  const kinds = safetyKindsFor(roles);
  const mayRecord = kinds.length > 0;

  const reload = useCallback(() => {
    setError(null);
    loadSafetyEntries(sb, tenantId)
      .then(async (r) => {
        setRows([...r].sort(safetyOrder));
        const ids = [...new Set(r.flatMap((e) => e.photo_ids))];
        setPhotos(await loadLinkedPhotos(sb, ids).catch(() => []));
      })
      .catch((e: unknown) => setError(message(e)));
  }, [sb, tenantId]);
  useEffect(reload, [reload]);
  useEffect(() => {
    if (!mayRecord) return;
    let live = true;
    void myStaffName(sb, tenantId).then((n) => live && setMe(n));
    return () => {
      live = false;
    };
  }, [sb, tenantId, mayRecord]);

  // the form is on the page before the print dialog opens
  useEffect(() => {
    if (printAsk > 0) print();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per press, not when `print` changes
  }, [printAsk]);

  const projects = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of rows ?? []) m.set(r.project_id, `${r.project_number} ${r.project_title}`.trim());
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);
  const inProject = (rows ?? []).filter((r) => registerRecordShown(r, { project }));
  const shown = inProject.filter(
    (r) => registerRecordShown(r, { kind, show: open }) && registerClientShown(r, client),
  );
  const totals = safetyTotals(inProject);
  const current = (rows ?? []).find((r) => r.id === selected) ?? null;
  const toPrint = (rows ?? []).find((r) => r.id === printing) ?? null;

  // decision 1034: the project's latest breach report PDF in a new tab; none sent yet, the project's Safety tab
  async function openBreachReport(projectId: string): Promise<void> {
    setNotice(null);
    try {
      if (await loadBreachReportPdf(sb, projectId)) {
        openTab(documentUrl({ doc: 'breach-report', project: projectId }));
        return;
      }
    } catch (e) {
      setNotice({ ok: false, text: `The breach report could not be opened. ${message(e)}` });
      return;
    }
    if (onOpenBreachReport) onOpenBreachReport(projectId);
    else window.location.assign(breachReportHref(projectId));
  }

  async function run(what: string, act: () => Promise<string>): Promise<void> {
    setBusy(what);
    setNotice(null);
    try {
      setNotice({ ok: true, text: await act() });
      reload();
    } catch (e) {
      setNotice({ ok: false, text: message(e) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="page page--wide">
      <PageHeader
        title="Safety register"
        subtitle={`The accidents, incidents, near misses and landholder complaints of ${tenantName}'s projects, recorded here or in Teslyn Field, and the height breaches of their breach reports, with the Safety Occurrence and Landholder Notification forms to print.`}
      >
        {mayRecord && !editing && (
          <Button
            variant="primary"
            onClick={() => {
              setNotice(null);
              setSelected(null);
              setEditing({
                id: null,
                projectId: project === 'all' ? '' : project,
                draft: {
                  ...newSafetyEntry(kinds.includes('incident') ? 'incident' : kinds[0]!, today),
                  reported_by_name: me,
                },
              });
            }}
          >
            Record an entry
          </Button>
        )}
      </PageHeader>
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      {editing && (
        <EntryForm
          sb={sb}
          tenantId={tenantId}
          editing={editing}
          kinds={kinds}
          busy={busy === 'save'}
          onChange={setEditing}
          onCancel={() => setEditing(null)}
          onSave={(e) =>
            void run('save', async () => {
              const saved = await saveSafetyEntry(sb, tenantId, e.projectId, e.draft, e.id);
              setEditing(null);
              setSelected(saved.id);
              return `“${saved.topic}” was saved. The project’s laptops get it when they sync.`;
            })
          }
        />
      )}
      {error && <ErrorState message={error} onRetry={reload} />}
      {!rows && !error && <Loading what="the safety register" />}
      {rows && (
        <div className="stack">
          {/* R215: one row of seven where the page is wide enough, 4 + 3 or two a row where not (styles.css) */}
          <div className="safety-totals-box">
            <ul className="metric-tiles safety-totals" aria-label="Totals">
              {[
                ...REGISTER_KINDS.map((k) => [REGISTER_KIND_TOTAL_WORDS[k], totals[k]] as const),
                ['Open', totals.open] as const,
              ].map(([label, n]) => (
                <li key={label} className="metric-tile">
                  <span className="label">{label}</span>
                  <span className="metric metric-tile__value">{n}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="row row--wrap" role="group" aria-label="Filters">
            {/* the shared field: label above its box (R208, decision 1030; ui-layout-rules.md rule 1) */}
            <SelectField<string>
              label="Project"
              value={project}
              onChange={setProject}
              options={[['all', 'All projects'], ...projects]}
            />
            <SelectField<'all' | RegisterKind>
              label="Kind"
              value={kind}
              onChange={setKind}
              options={[
                ['all', 'Every kind'],
                ...REGISTER_KINDS.map((k) => [k, REGISTER_KIND_WORDS[k]] as const),
              ]}
            />
            <SelectField<RegisterShow>
              label="Show"
              value={open}
              onChange={setOpen}
              options={REGISTER_SHOW.map((v) => [v, REGISTER_SHOW_WORDS[v]] as const)}
            />
            <SelectField<RegisterClientFilter>
              label="Client"
              value={client}
              onChange={setClient}
              options={REGISTER_CLIENT_FILTER.map((v) => [v, REGISTER_CLIENT_FILTER_WORDS[v]] as const)}
            />
          </div>
          <TableWrap>
            <table className="table">
              <thead>
                <tr>
                  <th className="label">Date</th>
                  <th className="label">Project</th>
                  <th className="label">Kind</th>
                  <th className="label">Topic</th>
                  <th className="label">Reported by</th>
                  <th className="label">State</th>
                  <th className="label">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && (
                  <tr>
                    <td colSpan={7} className="muted">
                      {rows.length === 0
                        ? `Nothing recorded yet. ${mayRecord ? 'Press "Record an entry", or record it in Teslyn Field, Safety register.' : SAFETY_WHO_RECORDS}`
                        : 'No entry matches these filters.'}
                    </td>
                  </tr>
                )}
                {shown.map((r) => (
                  <tr key={r.id} aria-selected={r.id === selected}>
                    <td className="data">
                      {printed(r.occurred_on)}
                      {r.occurred_at && <div className="caption muted">{r.occurred_at}</div>}
                    </td>
                    <td className="body">
                      <span className="data">{r.project_number}</span> {r.project_title}
                    </td>
                    <td className="body">
                      {REGISTER_KIND_WORDS[r.kind]}
                      {r.from_breach && <div className="caption muted">{BREACH_RECORD_SOURCE_WORDS}</div>}
                    </td>
                    <td className="body">{r.topic}</td>
                    <td className="caption">
                      {r.reported_by_name}
                      {r.reported_by_role && <div className="muted">{r.reported_by_role}</div>}
                    </td>
                    <td className="caption">
                      <StateChip record={r} today={today} />
                      {r.client?.shown && <div className="chip chip--brand">{REGISTER_APPROVED_WORDS}</div>}
                    </td>
                    <td className="table__actions">
                      <Button
                        variant="quiet"
                        aria-label={`Details of ${r.topic}`}
                        onClick={() => setSelected(r.id === selected ? null : r.id)}
                      >
                        {r.id === selected ? 'Hide details' : 'Details'}
                      </Button>
                      <Button
                        variant="quiet"
                        aria-label={`Print the form of ${r.topic}`}
                        onClick={() => {
                          setPrinting(r.id);
                          setPrintAsk((n) => n + 1);
                        }}
                      >
                        Print the form
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
          {current && (
            <Details
              entry={current}
              photos={photos}
              canChange={canChangeRegisterRecord(roles, current) && !editing}
              clientStep={editing ? null : registerClientStep(roles, current, current.client)}
              onShown={(shown) =>
                void run('client', async () => {
                  await setRegisterShown(sb, registerSourceOf(current), current.id, shown);
                  return registerClientDoneWords(current.topic, shown);
                })
              }
              busy={busy}
              onOpenBreachReport={(projectId) => void openBreachReport(projectId)}
              onEdit={() => {
                const entry = registerSafetyEntry(current);
                if (!entry) return;
                setNotice(null);
                setEditing({ id: entry.id, projectId: entry.project_id, draft: draftOf(entry) });
              }}
              onClose={(closing) =>
                void run('close', async () => {
                  await closeSafetyEntry(sb, current.id, closing ? me || 'Teslyn Cloud' : null);
                  return closing ? `“${current.topic}” was closed.` : `“${current.topic}” was reopened.`;
                })
              }
              onRemove={() =>
                void run('remove', async () => {
                  await removeSafetyEntry(sb, current.id);
                  setSelected(null);
                  return `“${current.topic}” was removed.`;
                })
              }
            />
          )}
        </div>
      )}
      {toPrint && <PrintForm entry={toPrint} tenantName={tenantName} photos={photos} />}
    </section>
  );
}

/**
 * The form to record or change an entry, as Teslyn Field's: the project (for a new entry), kind, date, time, topic,
 * who reported it, the people involved, the aircraft, the location and place, the landholder for a complaint, what
 * happened and the action, corrective action taken, the follow-up, and the project's photos to link.
 */
function EntryForm({
  sb,
  tenantId,
  editing,
  kinds,
  busy,
  onChange,
  onCancel,
  onSave,
}: {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly editing: Editing;
  readonly kinds: readonly SafetyKind[];
  readonly busy: boolean;
  readonly onChange: (e: Editing) => void;
  readonly onCancel: () => void;
  readonly onSave: (e: Editing) => void;
}): ReactElement {
  const [projects, setProjects] = useState<SafetyProject[] | null>(null);
  const [choices, setChoices] = useState<SafetyChoices | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [typed, setTyped] = useState('');
  const d = editing.draft;
  const set = (next: Partial<SafetyEntryDraft>): void => onChange({ ...editing, draft: { ...d, ...next } });

  useEffect(() => {
    let live = true;
    loadSafetyProjects(sb, tenantId)
      .then((p) => live && setProjects(p))
      .catch((e: unknown) => live && setLoadError(message(e)));
    return () => {
      live = false;
    };
  }, [sb, tenantId]);
  useEffect(() => {
    let live = true;
    setChoices(null);
    if (!editing.projectId) return;
    loadSafetyChoices(sb, tenantId, editing.projectId)
      .then((c) => live && setChoices(c))
      .catch((e: unknown) => live && setLoadError(message(e)));
    return () => {
      live = false;
    };
  }, [sb, tenantId, editing.projectId]);

  const people = (choices?.people ?? []).filter((p) => !p.deleted_at && p.active !== false);
  const crew: SafetyPerson[] = choices ? projectCrewOn(d.occurred_on, choices.crew, choices.people) : [];
  const complaint = d.kind === 'landholder_complaint';
  const title = editing.id ? 'Change the entry' : 'Record an entry';

  function save(): void {
    const out = checkSafetyEntry(d);
    if (!editing.projectId) out.unshift('Choose the project.');
    setProblems(out);
    if (out.length === 0) onSave(editing);
  }

  return (
    <form
      className="panel stack"
      aria-label={title}
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      <h2 className="heading">{title}</h2>
      {loadError && <p className="notice notice--fail">{loadError}</p>}
      <IssueList issues={problems.map((m) => ({ field: m, message: m }))} title="Not saved" />
      <div className="form-grid">
        {!editing.id && (
          <SelectField<string>
            label="Project"
            value={editing.projectId}
            onChange={(v) => onChange({ ...editing, projectId: v })}
            options={[
              ['', projects === null ? 'Loading the projects…' : 'Choose the project'],
              ...(projects ?? []).map((p) => [p.id, `${p.number} ${p.title}`.trim()] as const),
            ]}
          />
        )}
        <SelectField<SafetyKind>
          label="Kind"
          value={d.kind}
          onChange={(v) => set({ kind: v })}
          options={SAFETY_KINDS.filter((k) => kinds.includes(k)).map(
            (k) => [k, SAFETY_KIND_WORDS[k]] as const,
          )}
        />
        <TextField label="Date" type="date" value={d.occurred_on} onChange={(v) => set({ occurred_on: v })} />
        <TextField
          label="Time (HH:MM)"
          spellCheck={false}
          value={d.occurred_at}
          onChange={(v) => set({ occurred_at: v })}
        />
      </div>
      <TextField label="Topic" value={d.topic} onChange={(v) => set({ topic: v })} />
      <div className="form-grid">
        <SelectField<string>
          label="Reported by"
          value={d.reported_by_person_id ?? ''}
          onChange={(v) => {
            const p = people.find((x) => x.id === v);
            set(
              p ? { reported_by_person_id: p.id, reported_by_name: p.name } : { reported_by_person_id: null },
            );
          }}
          options={[
            ['', 'Someone outside the staff register (type the name)'],
            ...people.map((p) => [p.id, p.name] as const),
          ]}
        />
        <TextField
          label="Reporter's name"
          disabled={!!d.reported_by_person_id}
          value={d.reported_by_name}
          onChange={(v) => set({ reported_by_name: v })}
        />
        <TextField label="Role" value={d.reported_by_role} onChange={(v) => set({ reported_by_role: v })} />
      </div>
      <fieldset className="fieldset stack">
        <legend className="label">People involved</legend>
        {crew.map((p) => (
          <CheckField
            key={p.person_id}
            label={`${p.name} (${p.role})`}
            checked={d.people.some((x) => x.person_id === p.person_id)}
            onChange={(on) =>
              set({ people: on ? [...d.people, p] : d.people.filter((x) => x.person_id !== p.person_id) })
            }
          />
        ))}
        {d.people
          .filter((p) => !p.person_id || !crew.some((c) => c.person_id === p.person_id))
          .map((p, i) => (
            <p key={`${p.name}-${i}`} className="row caption">
              {p.role ? `${p.name} (${p.role})` : p.name}
              <Button variant="quiet" onClick={() => set({ people: d.people.filter((x) => x !== p) })}>
                Take off
              </Button>
            </p>
          ))}
        <div className="row">
          <TextField label="Someone else (name)" value={typed} onChange={setTyped} />
          <Button
            disabled={!typed.trim()}
            onClick={() => {
              set({ people: [...d.people, { person_id: null, name: typed.trim(), role: '' }] });
              setTyped('');
            }}
          >
            Add
          </Button>
        </div>
      </fieldset>
      <div className="form-grid">
        <SelectField<string>
          label="Aircraft"
          value={d.aircraft_id ?? ''}
          onChange={(v) => {
            const a = choices?.aircraft.find((x) => x.id === v);
            set(
              a
                ? { aircraft_id: a.id, aircraft_registration: a.registration }
                : { aircraft_id: null, aircraft_registration: '' },
            );
          }}
          options={[
            ['', d.aircraft_registration && !d.aircraft_id ? d.aircraft_registration : 'None'],
            ...(choices?.aircraft ?? [])
              .filter((a) => !a.deleted_at || a.id === d.aircraft_id)
              .map((a) => [a.id, a.registration] as const),
          ]}
        />
        <TextField
          label="Location or job number"
          spellCheck={false}
          value={d.location}
          onChange={(v) => set({ location: v })}
        />
        <TextField
          label="Latitude (WGS 84, optional)"
          spellCheck={false}
          value={d.lat === null ? '' : String(d.lat)}
          onChange={(v) => set({ lat: v.trim() === '' || !Number.isFinite(Number(v)) ? null : Number(v) })}
        />
        <TextField
          label="Longitude (WGS 84, optional)"
          spellCheck={false}
          value={d.lon === null ? '' : String(d.lon)}
          onChange={(v) => set({ lon: v.trim() === '' || !Number.isFinite(Number(v)) ? null : Number(v) })}
        />
      </div>
      {complaint && (
        <fieldset className="fieldset stack">
          <legend className="label">Landholder</legend>
          {(choices?.parcels.length ?? 0) > 0 && (
            <SelectField<string>
              label="Property in the landholder register"
              value={d.land_parcel_id ?? ''}
              onChange={(v) => {
                const p = choices?.parcels.find((x) => x.id === v);
                set(
                  p
                    ? { land_parcel_id: p.id, landholder_details: d.landholder_details || p.name }
                    : { land_parcel_id: null },
                );
              }}
              options={[
                ['', 'Not in the register'],
                ...(choices?.parcels ?? []).map((p) => [p.id, p.name] as const),
              ]}
            />
          )}
          <div className="form-grid">
            <TextField
              label="Landholder name"
              value={d.landholder_name}
              onChange={(v) => set({ landholder_name: v })}
            />
            <TextField
              label="Phone"
              type="tel"
              value={d.landholder_phone}
              onChange={(v) => set({ landholder_phone: v })}
            />
          </div>
          <TextField
            label="Details (the property, for example)"
            value={d.landholder_details}
            onChange={(v) => set({ landholder_details: v })}
          />
        </fieldset>
      )}
      <TextArea
        label="Description: what happened"
        rows={5}
        value={d.description}
        onChange={(v) => set({ description: v })}
      />
      <TextArea label={safetyActionWords(d.kind)} value={d.actions} onChange={(v) => set({ actions: v })} />
      <CheckField
        label="Corrective action has been taken"
        checked={d.corrective_action_taken}
        onChange={(v) => set({ corrective_action_taken: v })}
      />
      <TextArea
        label="Follow-up: what is still to be done, and what was done"
        rows={3}
        value={d.follow_up}
        onChange={(v) => set({ follow_up: v })}
      />
      <TextField
        label="Follow-up due"
        type="date"
        value={d.follow_up_due ?? ''}
        onChange={(v) => set({ follow_up_due: v || null })}
      />
      {editing.projectId && (
        <PhotoPicker
          photos={choices?.photos ?? []}
          linked={d.photo_ids}
          onChange={(photo_ids) => set({ photo_ids })}
        />
      )}
      <div className="row">
        <Button variant="primary" type="submit" busy={busy}>
          Save the entry
        </Button>
        <Button variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/** The project's photos to tick (decision 863, 5 B): live ones newest first, and linked ones since removed. */
function PhotoPicker({
  photos,
  linked,
  onChange,
}: {
  readonly photos: readonly ProjectPhoto[];
  readonly linked: readonly string[];
  readonly onChange: (ids: readonly string[]) => void;
}): ReactElement {
  const shown = [
    ...livePhotos([...photos]),
    ...photos.filter((p) => p.deleted_at !== null && linked.includes(p.id)),
  ];
  const full = linked.length >= SAFETY_LIMITS.photos;
  return (
    <fieldset className="fieldset stack">
      <legend className="label">Photos of the project (optional)</legend>
      {shown.length === 0 ? (
        <p className="caption muted">The project has no photos yet. Add them on the project’s Photos tab.</p>
      ) : (
        <ul className="stack" aria-label="Photos to link">
          {shown.map((p) => {
            const on = linked.includes(p.id);
            return (
              <li key={p.id}>
                <label className="row caption">
                  <input
                    type="checkbox"
                    aria-label={`Link ${p.file_name}`}
                    checked={on}
                    disabled={!on && full}
                    onChange={(e) =>
                      onChange(e.target.checked ? [...linked, p.id] : linked.filter((x) => x !== p.id))
                    }
                  />
                  <span className="data">{p.file_name}</span>
                  {p.caption.trim() && <span>{p.caption.trim()}</span>}
                  <span className="muted">
                    {photoCategoryLabel(p.category)}, {photoTakenText(p)}
                    {p.deleted_at ? ', removed from the project' : ''}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
      {full && <p className="caption muted">An entry links at most {SAFETY_LIMITS.photos} photos.</p>}
    </fieldset>
  );
}

/**
 * A record's state: open (follow-up overdue or not), closed, or withdrawn from the breach report (decision 1021). The
 * client portal's Safety register shows the same (R220).
 */
export function StateChip({
  record: r,
  today,
}: {
  readonly record: Pick<SafetyListRow, 'closed_at' | 'follow_up_due' | 'deleted_at' | 'from_breach'>;
  readonly today: string;
}): ReactElement {
  const state = registerRecordState(r, today);
  if (state === 'withdrawn')
    return <span className="chip">Withdrawn {printed(r.from_breach?.withdrawn?.at ?? r.deleted_at)}</span>;
  if (state === 'closed')
    return (
      <span className="chip chip--pass">
        {r.from_breach ? 'Reason recorded' : 'Closed'} {printed(r.closed_at)}
      </span>
    );
  if (state === 'overdue') return <span className="chip chip--fail">Open, follow-up overdue</span>;
  return <span className="chip chip--warn">Open</span>;
}

function Details({
  entry: e,
  photos,
  canChange,
  busy,
  onEdit,
  onClose,
  onRemove,
  onOpenBreachReport,
  clientStep,
  onShown,
}: {
  readonly entry: SafetyListRow;
  readonly photos: readonly LinkedPhoto[];
  readonly canChange: boolean;
  /** Decision 1023: tenant admins and managers choose what the client sees; null for others. */
  readonly clientStep: RegisterClientStep | null;
  readonly onShown: (shown: boolean) => void;
  readonly busy: string | null;
  readonly onEdit: () => void;
  readonly onClose: (closing: boolean) => void;
  readonly onRemove: () => void;
  /** "Open breach report" (R219, decision 1034): the latest PDF, else the project's Safety tab. */
  readonly onOpenBreachReport: (projectId: string) => void;
}): ReactElement {
  const [confirm, setConfirm] = useState(false);
  const b = e.from_breach ?? null;
  // the same lines as the client portal's (decision 1035), then whether the client has it
  const rows: [string, string][] = [
    ...registerDetailLines(e, photos),
    ['Client', registerClientLine(e.client)],
  ];
  return (
    <div className="panel stack" role="region" aria-label={`Entry: ${e.topic}`}>
      <div className="row row--wrap">
        <h2 className="heading">{e.topic}</h2>
        {canChange && (
          <>
            <Button variant="quiet" onClick={onEdit}>
              Change
            </Button>
            <Button busy={busy === 'close'} onClick={() => onClose(!e.closed_at)}>
              {e.closed_at ? 'Reopen' : 'Close'}
            </Button>
            {confirm ? (
              <>
                <Button variant="secondary" busy={busy === 'remove'} onClick={onRemove}>
                  Confirm remove
                </Button>
                <Button variant="quiet" onClick={() => setConfirm(false)}>
                  Keep
                </Button>
              </>
            ) : (
              <Button variant="quiet" onClick={() => setConfirm(true)}>
                Remove
              </Button>
            )}
          </>
        )}
      </div>
      {clientStep && (
        <div className="row row--wrap">
          <Button busy={busy === 'client'} onClick={() => onShown(clientStep === 'approve')}>
            {REGISTER_CLIENT_STEP_WORDS[clientStep]}
          </Button>
          <span className="caption muted">{REGISTER_CLIENT_SEES}</span>
        </div>
      )}
      {b && (
        <p className="notice" role="note">
          {BREACH_RECORD_SOURCE_WORDS}. {BREACH_RECORD_READ_ONLY}{' '}
          <a
            href={breachReportHref(e.project_id)}
            onClick={(ev) => {
              ev.preventDefault();
              onOpenBreachReport(e.project_id);
            }}
          >
            {BREACH_REPORT_OPEN_WORDS}
          </a>
        </p>
      )}
      <dl className="kv safety-details">
        {rows
          .filter(([, v]) => v.trim() !== '')
          .map(([k, v]) => (
            <div key={k} className="safety-details__row">
              <dt className="label">{k}</dt>
              <dd className="body safety-details__text">{v}</dd>
            </div>
          ))}
      </dl>
    </div>
  );
}

/**
 * TJR's form, the words from the domain (`safetyFormContent`); on screen it is hidden, the print stylesheet shows it.
 * It is put straight under `<body>`, outside the app (`#root`), so the print can drop the whole app (display: none)
 * and print the form alone from the top of page 1, wherever the register is scrolled to (MB's blank print of
 * 4 Nov 2026).
 */
function PrintForm({
  entry,
  tenantName,
  photos,
}: {
  readonly entry: SafetyListRow;
  readonly tenantName: string;
  readonly photos: readonly LinkedPhoto[];
}): ReactElement {
  const f = safetyFormContent(entry, photos);
  return createPortal(
    <article className="safety-print" aria-label={`${f.title} to print`}>
      <p className="caption">{tenantName}</p>
      <h1 className="title safety-print__title">{f.title}</h1>
      <dl className="body safety-print__fields">
        {f.fields.map(([k, v]) => (
          <div key={k} className="safety-print__field">
            <dt>{k}:</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      <h2 className="heading safety-print__heading">{f.description_heading}</h2>
      <p className="body safety-print__text">{f.description}</p>
      <h2 className="heading safety-print__heading">{f.action_heading}</h2>
      <p className="body safety-print__text">{f.action}</p>
      {f.extra.length > 0 && (
        <dl className="body safety-print__fields">
          {f.extra.map(([k, v]) => (
            <div key={k} className="safety-print__field">
              <dt>{k}:</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
      )}
      <section className="safety-print__office">
        <h2 className="heading safety-print__heading">Office Use Only</h2>
        {f.office.map((o) => (
          <p key={o} className="body safety-print__office-line">
            {o}
          </p>
        ))}
        <div className="caption safety-print__signature">
          {f.office_signature.map((s) => (
            <span key={s}>{s}</span>
          ))}
        </div>
      </section>
      <p className="caption">
        {entry.project_number} {entry.project_title}
      </p>
    </article>,
    document.body,
  );
}
